// components/customer/primary-picker/PrimaryPickerField.tsx
import { KeyboardEvent, ReactNode, useId, useState } from 'react';
import { ActionIcon, Box, Combobox, Input, ScrollArea, useCombobox } from '@mantine/core';
import { IconChevronDown, IconPencil, IconPlus, IconSearch } from '@tabler/icons-react';
import classes from './PrimaryPickerField.module.css';

export const SEARCH_THRESHOLD = 8;

export interface PrimaryPickerFieldProps<T> {
  label: string;
  noun: string;
  icon: ReactNode;
  items: T[];
  current?: T;
  getId: (item: T) => string;
  getName: (item: T) => string;
  getValueLines: (item: T) => { main: string; sub: string[] };
  getOptionSubLines: (item: T) => string[];
  // Other roles to tag the item with, next to "Primary" (e.g. a contact's "Accounting")
  getTags?: (item: T) => string[];
  getSearchText: (item: T) => string;
  searchPlaceholder: string;
  tagVariant: 'delivery' | 'postal';
  canEdit: boolean;
  valueActions?: (item: T) => ReactNode;
  onPick: (item: T) => void;
  onEdit: (item: T) => void;
  onAdd: () => void;
  testId: string;
}

// Keeps Enter/Space on a nested button from reaching the Combobox target's key handler.
// Escape is left alone so it still closes the panel.
const stopKeys = (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
};

const ADD_VALUE = '__add';

export default function PrimaryPickerField<T>({
  label, noun, icon, items, current, getId, getName, getValueLines, getOptionSubLines, getTags, getSearchText,
  searchPlaceholder, tagVariant, canEdit, valueActions, onPick, onEdit, onAdd, testId,
}: PrimaryPickerFieldProps<T>) {
  const labelId = useId();
  const valueId = useId();
  const [search, setSearch] = useState('');
  const showSearch = items.length > SEARCH_THRESHOLD;

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
    onDropdownOpen: () => {
      if (showSearch) combobox.focusSearchInput();
    },
  });

  if (items.length === 0) {
    return (
      <Box data-testid={testId} mt="var(--mantine-spacing-sm)">
        <Input.Label id={labelId} component="span">{label}</Input.Label>
        <Box
          className={classes.box}
          role="button"
          aria-labelledby={labelId}
          aria-describedby={valueId}
          aria-disabled={!canEdit || undefined}
          tabIndex={canEdit ? 0 : -1}
          data-inert={!canEdit || undefined}
          onClick={canEdit ? onAdd : undefined}
          onKeyDown={(e) => {
            if (canEdit && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onAdd();
            }
          }}
        >
          <span className={classes.icon}>{icon}</span>
          <div className={classes.col} id={valueId}>
            <div className={classes.main} data-muted>No {noun} yet</div>
            {canEdit && <div className={classes.addHint}>+ Add {noun}</div>}
          </div>
        </Box>
      </Box>
    );
  }

  const query = search.trim().toLowerCase();
  const matches = (item: T) => !query || getSearchText(item).toLowerCase().includes(query);
  const currentId = current ? getId(current) : undefined;
  const others = items
    .filter(i => getId(i) !== currentId)
    .sort((a, b) => getName(a).localeCompare(getName(b)))
    .filter(matches);
  const visibleCurrent = current && matches(current) ? current : undefined;

  const handleSubmit = (value: string) => {
    if (value === ADD_VALUE) {
      combobox.closeDropdown();
      onAdd();
      return;
    }
    const item = items.find(i => getId(i) === value);
    combobox.closeDropdown();
    if (item) onPick(item);
  };

  const editButton = (item: T, size: number) => (
    <ActionIcon
      variant="subtle"
      color="gray"
      size={size}
      aria-label={`Edit ${getName(item)}`}
      title={`Edit ${getName(item)}`}
      onMouseDown={(e) => e.preventDefault()}
      onKeyDown={stopKeys}
      onClick={(e) => {
        e.stopPropagation();
        combobox.closeDropdown();
        onEdit(item);
      }}
    >
      <IconPencil size={16} />
    </ActionIcon>
  );

  const rowContent = (item: T, isCurrent: boolean) => (
    <>
      <div className={classes.optBody}>
        <div className={classes.optName}>
          {getName(item)}
          {isCurrent && <span className={classes.tag} data-variant={tagVariant}>Primary</span>}
          {getTags?.(item).map(tag => <span key={tag} className={classes.tag} data-variant={tagVariant}>{tag}</span>)}
        </div>
        {getOptionSubLines(item).filter(Boolean).map((line, i) => (
          <div key={i} className={classes.optSub}>{line}</div>
        ))}
      </div>
      {!isCurrent && canEdit && <span className={classes.setLabel}>Set as primary</span>}
      {canEdit && editButton(item, 28)}
    </>
  );

  const staticRow = (item: T, isCurrent: boolean) => (
    <div
      key={getId(item)}
      className={classes.option}
      role="option"
      aria-label={getName(item)}
      aria-selected={isCurrent}
      data-current={isCurrent || undefined}
      data-inert={!isCurrent || undefined}
    >
      {rowContent(item, isCurrent)}
    </div>
  );

  const pickableRow = (item: T) => (
    <Combobox.Option key={getId(item)} value={getId(item)} className={classes.option} aria-label={getName(item)}>
      {rowContent(item, false)}
    </Combobox.Option>
  );

  const value = current ? getValueLines(current) : undefined;

  return (
    <Box data-testid={testId} mt="var(--mantine-spacing-sm)">
      <Input.Label id={labelId} component="span">{label}</Input.Label>
      <Combobox store={combobox} onOptionSubmit={handleSubmit} position="bottom-start" offset={4} withinPortal>
        <Combobox.Target targetType="button" withExpandedAttribute>
          <Box
            className={classes.box}
            role="button"
            tabIndex={0}
            aria-labelledby={labelId}
            aria-haspopup="listbox"
            aria-describedby={valueId}
            data-open={combobox.dropdownOpened || undefined}
            onClick={() => combobox.toggleDropdown()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && combobox.dropdownOpened && combobox.getSelectedOptionIndex() === -1) {
                combobox.closeDropdown();
              }
            }}
          >
            <span className={classes.icon}>{icon}</span>
            <div className={classes.col} id={valueId}>
              {value ? (
                <>
                  <div className={classes.main}>{value.main}</div>
                  <div className={classes.sub}>
                    {value.sub.filter(Boolean).map((s, i) => <span key={i}>{s}</span>)}
                  </div>
                </>
              ) : (
                <>
                  <div className={classes.main} data-muted>No primary {noun}</div>
                  <div className={classes.sub}>Choose one</div>
                </>
              )}
            </div>
            {current && valueActions && (
              <span onClick={(e) => e.stopPropagation()} onKeyDown={stopKeys}>
                {valueActions(current)}
              </span>
            )}
            {current && canEdit && editButton(current, 26)}
            <IconChevronDown size={18} className={classes.chev} data-open={combobox.dropdownOpened || undefined} />
          </Box>
        </Combobox.Target>

        <Combobox.Dropdown className={classes.dropdown}>
          {showSearch && (
            <div className={classes.search}>
              <Combobox.Search
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder={searchPlaceholder}
                leftSection={<IconSearch size={15} />}
                size="xs"
              />
            </div>
          )}
          <Combobox.Options>
            <ScrollArea.Autosize mah={340} type="scroll">
              <div className={classes.scroll}>
                {visibleCurrent && (
                  <>
                    <div className={classes.group}>Current primary</div>
                    {staticRow(visibleCurrent, true)}
                  </>
                )}
                {others.length > 0 && (
                  <>
                    <div className={classes.group}>Set another {noun} as primary</div>
                    {others.map(item => (canEdit ? pickableRow(item) : staticRow(item, false)))}
                  </>
                )}
                {!visibleCurrent && others.length === 0 && <div className={classes.empty}>No matches</div>}
              </div>
            </ScrollArea.Autosize>
            {canEdit && (
              <div className={classes.footer}>
                <Combobox.Option value={ADD_VALUE} className={classes.addOption} aria-label={`Add ${noun}`}>
                  <IconPlus size={15} />
                  <span>Add {noun}</span>
                </Combobox.Option>
              </div>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Box>
  );
}
